import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE, IntegrationSource } from '@/constants';
import { queryKeys } from '@/hooks/queryKeys';
import {
  IStudentCourse,
  IStudentCourseDateFilters,
  useGetStudentCourses,
} from '@/hooks/useGetStudentCourses';
import { useAppData } from '@/providers/AppDataProvider';
import { useLocale } from '@/providers/LocaleProvider';
import { useSnackbar } from '@/providers/SnackbarProvider';
import { isEnrollmentTerminal } from '@/utilities';
import { openStudentCertificatePdf } from '@/utilities/openCertificatePdf';
import { Menu, MenuItem } from '@mui/material';
import { GridPaginationModel } from '@mui/x-data-grid-pro';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useRef, useState } from 'react';
import {
  ManageTrainingTable,
  ManageTrainingTableRow,
  StudentCoursesDialogType,
} from '../table/ManageTrainingTable';
import { ConfirmUnenrolStudentDialog } from './ConfirmUnenrolStudentDialog';
import { Dialog } from './Dialog';
import { LearningHistoryDialog } from './LearningHistoryDialog';
import { SendReminderDialog } from './SendReminderDialog';
import { SetEnrolmentDateDialog, SetEnrolmentDateMode } from './SetEnrolmentDateDialog';

export type { ManageTrainingTableRow, StudentCoursesDialogType };

const mapCourseToRow = (course: IStudentCourse): ManageTrainingTableRow => ({
  // Server-supplied row key, not the SKU: a course the student took more than once returns one row per
  // LearnUpon enrolment and they all carry the same SKU, which would collide in the grid's row map.
  id: course.rowKey,
  courseSourceId: course.courseSourceId,
  courseId: course.courseId,
  courseName: course.courseName,
  status: course.status,
  isOverdue: course.isOverdue,
  dueDate: course.dueDate ?? undefined,
  courseAccessExpiresAt: course.courseAccessExpiresAt ?? undefined,
  completedDate: course.completedDate ?? undefined,
  progress: course.percentageCompleted,
  learnUponEnrollmentId: course.learnUponEnrollmentId,
  learnUponInstance: course.learnUponInstance,
  integrationId: course.integrationId,
  integrationSource: course.integrationSource,
  canSendReminder: course.canSendReminder,
  enrollable: course.enrollable,
  courseEnrollmentId: course.courseEnrollmentId ?? undefined,
  canUnenrol: course.canUnenrol,
  employeeTrainingId: course.employeeTrainingId ?? undefined,
  learningPathEnrollmentId: course.learningPathEnrollmentId ?? undefined,
  integrationEnrollmentId: course.integrationEnrollmentId ?? undefined,
  hasCertificate: course.hasCertificate,
  canSetDates: course.canSetDates,
});

const TITLE_KEYS: Record<StudentCoursesDialogType, string> = {
  enrolled: 'manage.studentCourses.enrolledTitle',
  completed: 'manage.studentCourses.completedTitle',
  inProgress: 'manage.studentCourses.inProgressTitle',
  notStarted: 'manage.studentCourses.notStartedTitle',
  failed: 'manage.studentCourses.failedTitle',
  overdue: 'manage.studentCourses.overdueTitle',
  pending: 'manage.studentCourses.pendingTitle',
};

const courseColumn = { type: ManageTrainingTable.columns.courseName, label: 'manage.course' };

const COLUMNS_BY_TYPE: Record<
  StudentCoursesDialogType,
  {
    type: (typeof ManageTrainingTable.columns)[keyof typeof ManageTrainingTable.columns];
    label?: string;
  }[]
> = {
  enrolled: [
    courseColumn,
    { type: ManageTrainingTable.columns.status },
    { type: ManageTrainingTable.columns.dueDate },
    { type: ManageTrainingTable.columns.expiry },
    { type: ManageTrainingTable.columns.actions },
  ],
  completed: [
    courseColumn,
    { type: ManageTrainingTable.columns.completedDate },
    { type: ManageTrainingTable.columns.expiry },
    { type: ManageTrainingTable.columns.actions },
  ],
  inProgress: [
    courseColumn,
    { type: ManageTrainingTable.columns.progress },
    { type: ManageTrainingTable.columns.dueDate },
    { type: ManageTrainingTable.columns.expiry },
    { type: ManageTrainingTable.columns.actions },
  ],
  notStarted: [
    courseColumn,
    { type: ManageTrainingTable.columns.dueDate },
    { type: ManageTrainingTable.columns.expiry },
    { type: ManageTrainingTable.columns.actions },
  ],
  failed: [
    courseColumn,
    { type: ManageTrainingTable.columns.dueDate },
    { type: ManageTrainingTable.columns.expiry },
    { type: ManageTrainingTable.columns.actions },
  ],
  overdue: [
    courseColumn,
    { type: ManageTrainingTable.columns.progress },
    { type: ManageTrainingTable.columns.dueDate },
    { type: ManageTrainingTable.columns.expiry },
    { type: ManageTrainingTable.columns.actions },
  ],
  pending: [
    courseColumn,
    { type: ManageTrainingTable.columns.dueDate },
    { type: ManageTrainingTable.columns.expiry },
    { type: ManageTrainingTable.columns.actions },
  ],
};

interface StudentCoursesDialogProps {
  open: boolean;
  onClose: () => void;
  type: StudentCoursesDialogType;
  employeeId?: string;
  studentName?: string;
  theme?: any;
  providerId?: string;
  facultyIds?: string[];
  courseIds?: string[];
  /** The drawer's four period filters, resolved to boundaries. Passed straight through so this
   *  drill-down lists the same enrolments the student's counters were built from. */
  dates?: IStudentCourseDateFilters;
  onLearningHistory?: (row: ManageTrainingTableRow) => void;
  onSendReminderSuccess?: (courseName?: string) => void;
}

export const StudentCoursesDialog = ({
  open,
  onClose,
  type,
  employeeId,
  studentName,
  theme,
  providerId,
  facultyIds,
  courseIds,
  dates,
  onLearningHistory,
  onSendReminderSuccess,
}: StudentCoursesDialogProps) => {
  const { translate } = useLocale();
  const { user } = useAppData();
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: DEFAULT_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [unenrolRow, setUnenrolRow] = useState<ManageTrainingTableRow | null>(null);
  const [historyRow, setHistoryRow] = useState<ManageTrainingTableRow | null>(null);
  const [reminderRow, setReminderRow] = useState<ManageTrainingTableRow | null>(null);
  const [dateDialog, setDateDialog] = useState<{
    mode: SetEnrolmentDateMode;
    row: ManageTrainingTableRow;
  } | null>(null);
  const menuRow = useRef<ManageTrainingTableRow | null>(null);

  const { data, isLoading } = useGetStudentCourses({
    employeeId: open ? employeeId : undefined,
    statusFilter: type,
    paginationModel,
    providerId,
    facultyIds,
    courseIds,
    ...dates,
  });

  const courses = useMemo(() => data?.courses?.data?.map(mapCourseToRow) ?? [], [data]);

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      if (isLoading) return;
      setPaginationModel(model);
    },
    [isLoading]
  );

  const handleCourseAction = (e: React.MouseEvent, row: ManageTrainingTableRow) => {
    menuRow.current = row;
    setMenuAnchor(e.currentTarget as HTMLElement);
  };

  const handleMenuClose = () => setMenuAnchor(null);

  const handleClose = () => {
    setPaginationModel({ page: DEFAULT_PAGE, pageSize: DEFAULT_PAGE_SIZE });
    onClose();
  };

  const isAllowEnrollActions =
    user?.isAllowEnrollStudents && menuRow.current?.integrationSource !== IntegrationSource.Manual;

  const isAllowUnenrollActions =
    user?.isAllowUnenrollStudents &&
    menuRow.current?.integrationSource !== IntegrationSource.Manual;

  const isTerminalStatus = isEnrollmentTerminal(menuRow.current?.status);
  const isAllowReminder =
    isAllowEnrollActions && !isTerminalStatus && !!menuRow.current?.canSendReminder;
  const isAllowUnenrol =
    isAllowUnenrollActions && !isTerminalStatus && !!menuRow.current?.canUnenrol;

  const showViewCertificate = !!menuRow.current?.hasCertificate;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      theme={theme}
      fullWidth
      title={translate(TITLE_KEYS[type])}
      maxWidth='md'
      closeButtonSx={{
        display: 'block',
      }}
      contentSx={{
        paddingBottom: { xs: '20px !important', md: '40px !important' },
      }}
    >
      <ManageTrainingTable
        rows={courses}
        paper={false}
        theme={theme}
        translate={translate}
        dialogType={type}
        columns={COLUMNS_BY_TYPE[type]}
        pagination={true}
        paginationMode='server'
        totalCount={data?.courses?.totalCount ?? 0}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        showFooter={true}
        onRowAction={handleCourseAction}
        noRowsMessage={translate('manage.studentCourses.noCourses')}
        isLoading={isLoading}
        sortable={false}
        showActions={(row) => true}
      />

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {menuRow.current?.canSetDates
          ? [
              <MenuItem
                key='set-due-date'
                onClick={() => {
                  setDateDialog({ mode: 'dueDate', row: menuRow.current! });
                  handleMenuClose();
                }}
              >
                {translate('manage.setEnrolmentDate.setDueDate')}
              </MenuItem>,
              <MenuItem
                key='set-expiry-date'
                onClick={() => {
                  setDateDialog({ mode: 'expiry', row: menuRow.current! });
                  handleMenuClose();
                }}
              >
                {translate('manage.setEnrolmentDate.setExpiryDate')}
              </MenuItem>,
            ]
          : null}
        <MenuItem
          onClick={() => {
            const row = menuRow.current;
            handleMenuClose();
            setHistoryRow(row);
            onLearningHistory?.(row!);
          }}
        >
          {translate('manage.courseStudents.learningHistory')}
        </MenuItem>
        {showViewCertificate ? (
          <MenuItem
            onClick={() => {
              const row = menuRow.current;
              handleMenuClose();
              if (!employeeId || !row) return;
              openStudentCertificatePdf({
                employeeId,
                sku: row.courseSourceId ?? '',
                enrollmentId: row.learnUponEnrollmentId,
                instance: row.learnUponInstance,
                onProcessing: () =>
                  showSnackbar(translate('myKlayo.certificateProcessing'), 'info'),
                onError: () => showSnackbar(translate('myKlayo.certificateUnavailable'), 'error'),
              });
            }}
          >
            {translate('manage.courseStudents.viewCertificate')}
          </MenuItem>
        ) : null}
        {isAllowReminder ? (
          <MenuItem
            onClick={() => {
              setReminderRow(menuRow.current);
              handleMenuClose();
            }}
          >
            {translate('manage.courseStudents.sendReminder')}
          </MenuItem>
        ) : null}
        {isAllowUnenrol ? (
          <MenuItem
            onClick={() => {
              setUnenrolRow(menuRow.current);
              handleMenuClose();
            }}
          >
            {translate('manage.courseStudents.unenrol')}
          </MenuItem>
        ) : null}
      </Menu>

      <SetEnrolmentDateDialog
        open={!!dateDialog}
        mode={dateDialog?.mode ?? 'dueDate'}
        studentName={studentName}
        courseName={dateDialog?.row.courseName}
        courseEnrollmentId={dateDialog?.row.courseEnrollmentId}
        dueDate={dateDialog?.row.dueDate}
        expiryDate={dateDialog?.row.courseAccessExpiresAt}
        theme={theme}
        onClose={() => setDateDialog(null)}
        onSuccess={() => {
          // A due-date change can flip a row in or out of Overdue, so refetch this dialog's list,
          // the students table behind it, AND the summary stat cards — the summary query has no
          // queryKeys entry (useGetStudentStats derives its key from the URL), so invalidate by
          // URL prefix.
          queryClient.invalidateQueries({ queryKey: queryKeys.studentCourses.list() });
          queryClient.invalidateQueries({ queryKey: queryKeys.trainingStudents.list() });
          queryClient.invalidateQueries({ queryKey: ['/TrainingManager/students/summary'] });
        }}
      />

      {unenrolRow && (
        <ConfirmUnenrolStudentDialog
          row={{
            ...unenrolRow,
            employeeId: employeeId ?? '',
            name: studentName ?? '',
          }}
          courseName={unenrolRow.courseName}
          theme={theme}
          onClose={() => setUnenrolRow(null)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: queryKeys.studentCourses.list() });
            queryClient.invalidateQueries({ queryKey: queryKeys.trainingStudents.list() });
          }}
        />
      )}

      <LearningHistoryDialog
        open={!!historyRow}
        onClose={() => setHistoryRow(null)}
        studentName={studentName}
        courseName={historyRow?.courseName}
        courseEnrollmentId={historyRow?.courseEnrollmentId}
        employeeTrainingId={historyRow?.employeeTrainingId}
        learningPathEnrollmentId={historyRow?.learningPathEnrollmentId}
        integrationEnrollmentId={historyRow?.integrationEnrollmentId}
        theme={theme}
      />

      <SendReminderDialog
        open={!!reminderRow}
        employeeId={employeeId}
        studentName={studentName}
        courseId={reminderRow?.courseId ?? undefined}
        courseName={reminderRow?.courseName}
        theme={theme}
        onClose={() => setReminderRow(null)}
        onSuccess={() => onSendReminderSuccess?.(reminderRow?.courseName)}
      />
    </Dialog>
  );
};
